// ===============================================================
// ProBank — Payment Success Page
//
// Registration flow (Paystack):
//   1. Shows live, animated verification stages so the user can
//      watch their payment being confirmed in real time.
//   2. On success, displays the account summary and counts down.
//   3. Automatically redirects to the login page — no click
//      required. A manual button is provided as a fallback.
//
// Other flows (subscription / manual bank transfer):
//   Preserved payment-proof submission form.
// ===============================================================

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API from "../utils/api";

// ---------------------------------------------------------------
// Verification stages shown to the user during registration.
// Each stage is displayed briefly so the process feels real and
// transparent instead of an instant flash.
// ---------------------------------------------------------------

const VERIFY_STAGES = [
  { icon: "📡", label: "Confirming payment with Paystack" },
  { icon: "🔐", label: "Verifying transaction details" },
  { icon: "🛡️", label: "Running security checks" },
  { icon: "👤", label: "Creating your account" },
];

const STAGE_INTERVAL_MS = 1600; // time each stage stays visible
const REDIRECT_COUNTDOWN_S = 8; // seconds before auto-redirect

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const paymentType = params.get("type");
  const isRegistration = paymentType === "registration";

  // ---------- Registration verification state ----------
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [paymentMessage, setPaymentMessage] = useState("");
  const [verifyAttempt, setVerifyAttempt] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [countdown, setCountdown] = useState(null);

  // ---------- Manual payment-proof form state ----------
  const [user, setUser] = useState(null);
  const [showCopied, setShowCopied] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [bankUsed, setBankUsed] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const supportEmail = "probanksupport@gmail.com";

  const verificationStarted = useRef(false);

  // Load user details for the manual-payment workflow.
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("user")) || {};
      const userId =
        localStorage.getItem("userId") || stored._id || stored.id || "";

      setUser({
        name: stored.name || stored.fullName || "",
        email: stored.email || "",
        phone: stored.phone || "",
        plan: stored.plan || "",
        country: stored.country || "",
        userId,
      });
    } catch (error) {
      console.error("Could not load user from storage:", error);
      setUser({});
    }
  }, []);

  // ---------------------------------------------------------------
  // Stage animation — cycles through VERIFY_STAGES while the
  // backend verification is in progress.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (paymentStatus !== "checking") return;

    setCurrentStage(0);

    const interval = setInterval(() => {
      setCurrentStage((stage) =>
        stage < VERIFY_STAGES.length - 1 ? stage + 1 : stage
      );
    }, STAGE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [paymentStatus]);

  // ---------------------------------------------------------------
  // Backend verification — runs once the stages have been shown,
  // so the user always sees the process even when the API is fast.
  //
  // Paystack appends "reference" (and "trxref") to the callback
  // URL. The backend re-verifies it directly with Paystack's API.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!isRegistration) return;

    const callbackParams = new URLSearchParams(location.search);

    const reference =
      callbackParams.get("reference") ||
      callbackParams.get("trxref") ||
      callbackParams.get("tx_ref");

    let cancelled = false;
    let timer = null;

    const verifyPayment = async () => {
      if (!reference) {
        setPaymentStatus("pending");
        setPaymentMessage(
          "We could not find your payment reference. If you have already paid, please contact support before attempting another payment."
        );
        return;
      }

      try {
        const response = await API.post(
          "/payments/paystack/verify-registration",
          { reference }
        );

        if (cancelled) return;

        if (response.data?.success) {
          setPaymentStatus("success");
          setPaymentMessage(
            "Your payment has been confirmed and your account has been created successfully."
          );
        } else {
          setPaymentStatus("pending");
          setPaymentMessage(
            response.data?.message ||
              "Your payment has not been confirmed yet. Please retry verification shortly."
          );
        }
      } catch (error) {
        if (cancelled) return;

        console.error(
          "Paystack registration verification error:",
          error.response?.data || error.message
        );

        setPaymentStatus("pending");
        setPaymentMessage(
          error.response?.data?.message ||
            "We could not verify your payment right now. If money was deducted, do not pay again. Please retry verification shortly or contact support."
        );
      }
    };

    // Retry clicks verify immediately; the first attempt waits
    // until the stage sequence has played through.
    if (verifyAttempt > 0) {
      verificationStarted.current = false;
      verifyPayment();
    } else {
      timer = setTimeout(
        verifyPayment,
        STAGE_INTERVAL_MS * VERIFY_STAGES.length
      );
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isRegistration, location.search, verifyAttempt]);

  // ---------------------------------------------------------------
  // Countdown + automatic redirect to login after success.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (paymentStatus !== "success" || !isRegistration) return;

    setCountdown(REDIRECT_COUNTDOWN_S);

    const interval = setInterval(() => {
      setCountdown((seconds) => {
        if (seconds <= 1) {
          clearInterval(interval);
          navigate("/login", { replace: true });
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [paymentStatus, isRegistration, navigate]);

  // ---------------------------------------------------------------
  // Manual payment-proof handlers (preserved).
  // ---------------------------------------------------------------

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setSubmitError(
        "Please choose an image file (screenshot of your payment)."
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSubmitError(
        "Image is too large. Please choose a file under 5MB."
      );
      return;
    }

    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }

    setSubmitError("");
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const handleSubmitProof = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!screenshot) {
      setSubmitError("Please attach a screenshot of your payment.");
      return;
    }

    if (!amountPaid.trim()) {
      setSubmitError("Please enter the amount you paid.");
      return;
    }

    if (!user?.name || !user?.email) {
      setSubmitError(
        "We couldn't detect your account details. Please log in again and retry."
      );
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("screenshot", screenshot);
      formData.append("name", user.name);
      formData.append("email", user.email);
      formData.append("userId", user.userId || "");
      formData.append("amountPaid", amountPaid.trim());
      formData.append("bankUsed", bankUsed.trim());
      formData.append("plan", paymentType || user.plan || "");
      formData.append("country", user.country || "");
      formData.append("paidAt", new Date().toLocaleString());
      formData.append("notes", notes.trim());

      await API.post("/payment-proof/submit", formData);

      setSubmitted(true);
    } catch (error) {
      console.error("Proof submit error:", error);

      setSubmitError(
        error.response?.data?.message ||
          "Something went wrong submitting your proof. Please try again, or email us directly below."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (isRegistration) {
      navigate("/login");
    } else {
      navigate("/dashboard");
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2500);
    } catch (error) {
      console.error("Could not copy support email:", error);
    }
  };

  const emailSubject = encodeURIComponent(
    `Payment Proof - ${user?.name || user?.email || "User"}`
  );

  const emailBody = encodeURIComponent(
    `Hello Support,\n\nI have completed my payment. Please find my payment proof attached.\n\n` +
      `Name: ${user?.name || ""}\nEmail: ${user?.email || ""}\n` +
      `Amount paid: ${amountPaid}\nBank used: ${bankUsed}\n` +
      `Date paid: ${new Date().toLocaleString()}\n\n` +
      `Kindly verify and activate my account.\n\nThank you.`
  );

  const mailtoLink = `mailto:${supportEmail}?subject=${emailSubject}&body=${emailBody}`;

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-black to-gray-900 text-white flex items-center justify-center p-4 relative overflow-hidden py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(34,197,94,0.15),_transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,_rgba(59,130,246,0.08),_transparent_60%)] pointer-events-none" />

      {showCopied && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-6 right-6 z-50 bg-green-500 text-black px-5 py-3 rounded-xl text-sm font-semibold shadow-xl"
        >
          Email copied!
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl relative"
      >
        {isRegistration ? (
          <>
            {/* ---------- Status icon ---------- */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="flex justify-center mb-4"
            >
              <div
                className={`w-20 h-20 rounded-full border-2 flex items-center justify-center ${
                  paymentStatus === "success"
                    ? "bg-green-500/20 border-green-400"
                    : paymentStatus === "pending" ||
                      paymentStatus === "failed"
                    ? "bg-yellow-500/20 border-yellow-400"
                    : "bg-blue-500/20 border-blue-400"
                }`}
              >
                <span className="text-5xl">
                  {paymentStatus === "success"
                    ? "✅"
                    : paymentStatus === "pending" ||
                      paymentStatus === "failed"
                    ? "⚠️"
                    : "🔒"}
                </span>
              </div>
            </motion.div>

            {/* ---------- Live verification stages ---------- */}
            {paymentStatus === "checking" && (
              <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-white text-center mb-6">
                  Processing Your Payment
                </h1>

                <div className="space-y-3">
                  {VERIFY_STAGES.map((stage, index) => {
                    const isDone = index < currentStage;
                    const isActive = index === currentStage;

                    return (
                      <motion.div
                        key={stage.label}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{
                          opacity: isDone || isActive ? 1 : 0.35,
                          x: 0,
                        }}
                        transition={{ delay: index * 0.15 }}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${
                          isActive
                            ? "bg-blue-500/10 border-blue-400/40"
                            : isDone
                            ? "bg-green-500/10 border-green-400/30"
                            : "bg-white/5 border-white/10"
                        }`}
                      >
                        <span className="text-xl">{stage.icon}</span>

                        <span
                          className={`text-sm font-medium flex-1 ${
                            isActive
                              ? "text-blue-200"
                              : isDone
                              ? "text-green-300"
                              : "text-gray-400"
                          }`}
                        >
                          {stage.label}
                        </span>

                        {isDone && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-green-400 font-bold"
                          >
                            ✓
                          </motion.span>
                        )}

                        {isActive && (
                          <span className="w-4 h-4 border-2 border-white/20 border-t-blue-400 rounded-full animate-spin" />
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                <p className="text-center text-xs text-gray-500 mt-5">
                  Please do not close this page…
                </p>
              </div>
            )}

            {/* ---------- Success + countdown ---------- */}
            {paymentStatus === "success" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-center mb-6">
                  <h1 className="text-2xl sm:text-3xl font-bold text-green-400 mb-2">
                    Account Created Successfully! 🎉
                  </h1>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {paymentMessage}
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-5 text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Account status</span>
                    <span className="text-green-400 font-semibold">
                      Active
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Payment</span>
                    <span className="text-green-400 font-semibold">
                      Confirmed ✓
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Plan activation</span>
                    <span className="text-yellow-400 font-semibold">
                      Pending admin approval
                    </span>
                  </div>
                </div>

                {/* Countdown */}
                <div className="text-center mb-5">
                  <div className="relative inline-flex items-center justify-center">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                      <circle
                        cx="18"
                        cy="18"
                        r="15.5"
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="3"
                      />
                      <motion.circle
                        cx="18"
                        cy="18"
                        r="15.5"
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="97.4"
                        animate={{
                          strokeDashoffset:
                            97.4 * (1 - countdown / REDIRECT_COUNTDOWN_S),
                        }}
                        transition={{ duration: 0.9, ease: "linear" }}
                      />
                    </svg>
                    <span className="absolute text-xl font-bold text-green-400">
                      {countdown}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs mt-2">
                    Redirecting you to the login page…
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/login", { replace: true })}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 font-bold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  Log In Now →
                </button>
              </motion.div>
            )}

            {/* ---------- Pending / failed ---------- */}
            {(paymentStatus === "pending" || paymentStatus === "failed") && (
              <div>
                <div className="text-center mb-6">
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    {paymentStatus === "pending"
                      ? "Payment Confirmation Pending"
                      : "Payment Verification Needs Attention"}
                  </h1>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {paymentMessage}
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setVerifyAttempt((n) => n + 1)}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 font-bold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Retry Verification
                  </button>

                  <a
                    href={`mailto:${supportEmail}?subject=${encodeURIComponent(
                      "Payment Verification Assistance"
                    )}`}
                    className="block w-full text-center py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition"
                  >
                    Contact Support
                  </a>
                </div>

                <p className="mt-5 text-center text-xs text-gray-500">
                  If money was deducted, please do not pay again just
                  because verification is delayed.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* ---------- Manual / subscription flow (preserved) ---------- */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="flex justify-center mb-4"
            >
              <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center">
                <span className="text-5xl">{submitted ? "🎉" : "✅"}</span>
              </div>
            </motion.div>

            <div className="text-center mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-green-400 mb-2">
                {submitted ? "Proof Submitted!" : "Payment Received!"}
              </h1>
              <p className="text-gray-400 text-sm">
                {submitted
                  ? "Our team will review it and activate your account shortly."
                  : "We're awaiting confirmation to activate your account."}
              </p>
            </div>

            {user?.name && (
              <div className="p-3 rounded-xl bg-white/5 border border-white/10 mb-5 text-center">
                <p className="text-xs text-gray-400">Account</p>
                <p className="text-white font-semibold">{user.name}</p>
                {user.email && (
                  <p className="text-gray-400 text-xs">{user.email}</p>
                )}
              </div>
            )}

            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="p-4 rounded-2xl bg-yellow-500/15 border-2 border-yellow-400/50 mb-5">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">📸</span>
                      <div className="space-y-1">
                        <p className="font-bold text-yellow-300 text-sm">
                          Submit proof to activate faster
                        </p>
                        <p className="text-yellow-100/90 text-xs leading-relaxed">
                          Attach a screenshot of your payment and a few
                          details below. Our team reviews submissions
                          directly from this form.
                        </p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmitProof} className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">
                        Payment Screenshot
                      </label>

                      <label
                        htmlFor="proof-screenshot"
                        className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/20 hover:border-green-400/50 rounded-xl p-4 cursor-pointer transition bg-black/20"
                      >
                        {screenshotPreview ? (
                          <img
                            src={screenshotPreview}
                            alt="Payment proof preview"
                            className="max-h-48 rounded-lg object-contain"
                          />
                        ) : (
                          <>
                            <span className="text-3xl">📎</span>
                            <span className="text-sm text-gray-300">
                              Tap to attach screenshot
                            </span>
                            <span className="text-xs text-gray-500">
                              JPG, PNG or GIF — max 5MB
                            </span>
                          </>
                        )}
                      </label>

                      <input
                        id="proof-screenshot"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">
                        Amount Paid
                      </label>
                      <input
                        type="text"
                        value={amountPaid}
                        onChange={(e) => setAmountPaid(e.target.value)}
                        placeholder="e.g. 10,200 or 120 GHS"
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-green-400/50"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">
                        Bank / Method Used
                      </label>
                      <input
                        type="text"
                        value={bankUsed}
                        onChange={(e) => setBankUsed(e.target.value)}
                        placeholder="e.g. GTBank, Zenith, Card"
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-green-400/50"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wider">
                        Notes (optional)
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        placeholder="Anything else we should know?"
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 outline-none focus:border-green-400/50 resize-none"
                      />
                    </div>

                    {submitError && (
                      <div className="p-3 rounded-xl bg-red-500/10 border border-red-400/40 text-red-300 text-sm">
                        {submitError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 font-bold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100"
                    >
                      {submitting ? "Uploading..." : "📨 Submit Payment Proof"}
                    </button>
                  </form>

                  <div className="mt-5 pt-5 border-t border-white/10">
                    <p className="text-xs text-gray-500 text-center mb-3">
                      Prefer email? You can also send your proof directly:
                    </p>

                    <div className="p-3 rounded-xl bg-black/40 border border-white/10 mb-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-white font-mono text-sm font-semibold break-all">
                          {supportEmail}
                        </p>
                        <button
                          type="button"
                          onClick={copyEmail}
                          className="flex-shrink-0 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2 rounded-lg transition"
                        >
                          📋 COPY
                        </button>
                      </div>
                    </div>

                    <a
                      href={mailtoLink}
                      className="w-full block text-center py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition"
                    >
                      ✉️ Send via Email Instead
                    </a>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="done"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-400/30 text-green-200 text-sm text-center">
                    Your screenshot and details were received. We'll notify
                    you once your account is activated.
                  </div>

                  <button
                    type="button"
                    onClick={goNext}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 font-bold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Continue to Dashboard →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {!submitted && (
              <button
                type="button"
                onClick={goNext}
                className="w-full mt-3 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 text-sm font-medium transition"
              >
                Skip for now — Go to Dashboard
              </button>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
